export const VALID_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg'];

/**
 * Adds a cache-busting parameter to a URL to force a fresh request.
 * This bypasses browser cache which may have responses without proper CORS headers.
 *
 * Useful for:
 * - PDF generation with html2canvas where cached images may lack CORS headers
 * - Forcing image refresh after updates
 * - Avoiding stale cached content from CDN (e.g., Cloudflare R2)
 *
 * @param url - The URL to add cache-busting to
 * @returns The URL with a cache-busting query parameter, or the original URL if it's a data URL
 *
 * @example
 * addImageCacheBuster('https://cdn.example.com/image.jpg')
 * // Returns: 'https://cdn.example.com/image.jpg?_cb=1234567890'
 *
 * addImageCacheBuster('https://cdn.example.com/image.jpg?size=large')
 * // Returns: 'https://cdn.example.com/image.jpg?size=large&_cb=1234567890'
 */
export const addImageCacheBuster = (url: string): string => {
  if (!url || url.startsWith('data:')) return url;

  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}_cb=${Date.now()}`;
};

export const compressBase64Image = (
  base64: string,
  maxWidth: number,
  maxHeight: number,
  quality: number = 0.7
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;
        if (aspectRatio > 1) {
          width = maxWidth;
          height = maxWidth / aspectRatio;
        } else {
          height = maxHeight;
          width = maxHeight * aspectRatio;
        }
      }

      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      const compressedBase64 = canvas.toDataURL('image/jpeg', quality);
      resolve(compressedBase64);
    };

    img.onerror = (err) => reject(err);
    img.src = base64;
  });
};
